import type { SVGProps } from 'react';

type IconProps = SVGProps<SVGSVGElement> & { size?: number };

function base(size: number, props: SVGProps<SVGSVGElement>) {
  return {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.75,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    ...props,
  };
}

export function PenIcon({ size = 20, ...p }: IconProps) {
  return (
    <svg {...base(size, p)}>
      <path d="M14.5 4.5l5 5L8 21H3v-5L14.5 4.5z" />
      <path d="M13 6l5 5" />
    </svg>
  );
}

export function ChalkIcon({ size = 20, ...p }: IconProps) {
  // Stubby chalk stick
  return (
    <svg {...base(size, p)}>
      <rect x="6" y="4" width="6" height="16" rx="1.5" transform="rotate(18 9 12)" />
      <path d="M8.3 6.2l1.9.6" opacity=".55" />
    </svg>
  );
}

export function EraserIcon({ size = 20, ...p }: IconProps) {
  return (
    <svg {...base(size, p)}>
      <path d="M16 3l5 5-10 10H6l-3-3 13-12z" />
      <path d="M8.5 10.5l5 5" />
    </svg>
  );
}

export function AreaEraseIcon({ size = 20, ...p }: IconProps) {
  return (
    <svg {...base(size, p)}>
      <rect x="3" y="3" width="18" height="18" rx="2" strokeDasharray="3 2.5" />
      <path d="M9 15l6-6M10 9l5 6" />
    </svg>
  );
}

export function ChevronDownIcon({ size = 16, ...p }: IconProps) {
  return (
    <svg {...base(size, p)}>
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}

export function SettingsIcon({ size = 18, ...p }: IconProps) {
  return (
    <svg {...base(size, p)}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.7 1.7 0 00.3 1.8l.1.1a2 2 0 11-2.8 2.8l-.1-.1a1.7 1.7 0 00-1.8-.3 1.7 1.7 0 00-1 1.5V21a2 2 0 01-4 0v-.1a1.7 1.7 0 00-1.1-1.5 1.7 1.7 0 00-1.8.3l-.1.1a2 2 0 11-2.8-2.8l.1-.1a1.7 1.7 0 00.3-1.8 1.7 1.7 0 00-1.5-1H3a2 2 0 010-4h.1A1.7 1.7 0 004.6 9a1.7 1.7 0 00-.3-1.8l-.1-.1a2 2 0 112.8-2.8l.1.1a1.7 1.7 0 001.8.3H9a1.7 1.7 0 001-1.5V3a2 2 0 014 0v.1a1.7 1.7 0 001 1.5 1.7 1.7 0 001.8-.3l.1-.1a2 2 0 112.8 2.8l-.1.1a1.7 1.7 0 00-.3 1.8V9a1.7 1.7 0 001.5 1H21a2 2 0 010 4h-.1a1.7 1.7 0 00-1.5 1z" />
    </svg>
  );
}

export function BoardIcon({ size = 18, ...p }: IconProps) {
  return (
    <svg {...base(size, p)}>
      <rect x="3" y="4" width="18" height="14" rx="2" />
      <path d="M9 20h6M12 18v2" />
    </svg>
  );
}

export function UsersIcon({ size = 16, ...p }: IconProps) {
  return (
    <svg {...base(size, p)}>
      <path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
    </svg>
  );
}

export function ArrowLeftIcon({ size = 16, ...p }: IconProps) {
  return (
    <svg {...base(size, p)}>
      <path d="M19 12H5M12 19l-7-7 7-7" />
    </svg>
  );
}

export function CheckIcon({ size = 14, ...p }: IconProps) {
  return (
    <svg {...base(size, p)}>
      <path d="M5 12l5 5L20 7" />
    </svg>
  );
}

export function PlusIcon({ size = 16, ...p }: IconProps) {
  return (
    <svg {...base(size, p)}>
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

export function RefreshIcon({ size = 14, ...p }: IconProps) {
  return (
    <svg {...base(size, p)}>
      <path d="M21 12a9 9 0 01-15.36 6.36L3 16M3 12A9 9 0 0118.36 5.64L21 8" />
      <path d="M21 3v5h-5M3 21v-5h5" />
    </svg>
  );
}

export function AlertIcon({ size = 16, ...p }: IconProps) {
  return (
    <svg {...base(size, p)}>
      <path d="M10.3 3.86l-8.11 14a2 2 0 001.73 3h16.16a2 2 0 001.73-3l-8.11-14a2 2 0 00-3.4 0z" />
      <path d="M12 9v4M12 17h.01" />
    </svg>
  );
}

export function DownloadIcon({ size = 18, ...p }: IconProps) {
  return (
    <svg {...base(size, p)} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}

export function UndoIcon({ size = 18, ...p }: IconProps) {
  return (
    <svg {...base(size, p)}>
      <path d="M9 14L4 9l5-5" />
      <path d="M4 9h10.5a5.5 5.5 0 0 1 0 11H11" />
    </svg>
  );
}

export function FitIcon({ size = 18, ...p }: IconProps) {
  return (
    <svg {...base(size, p)}>
      <path d="M4 9V5.5A1.5 1.5 0 0 1 5.5 4H9M15 4h3.5A1.5 1.5 0 0 1 20 5.5V9M20 15v3.5a1.5 1.5 0 0 1-1.5 1.5H15M9 20H5.5A1.5 1.5 0 0 1 4 18.5V15" />
      <circle cx="12" cy="12" r="1.6" />
    </svg>
  );
}

/**
 * 南哪小黑板 logo. A chalk squiggle on the board-green square with a chalk
 * stick — deliberately brand-neutral (no school affiliation). Uses currentColor
 * for the frame so the mark adapts to the surface it's placed on; the green
 * square is the constant brand accent.
 */
export function LogoMark({ size = 40, ...p }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...p}
    >
      <rect x="2" y="2" width="44" height="44" rx="11" fill="#6D4C8C" />
      <rect
        x="2"
        y="2"
        width="44"
        height="44"
        rx="11"
        stroke="currentColor"
        strokeOpacity=".22"
        strokeWidth="1"
      />
      {/* Chalk squiggle — the doodle itself */}
      <path
        d="M10 24c3-6.5 6.5-6.5 9.5 0s6.5 6.5 9.5 0 5-4.5 9-1.5"
        stroke="#fff"
        strokeWidth="3"
        strokeLinecap="round"
        fill="none"
      />
      {/* Chalk stick resting at the bottom */}
      <rect
        x="29"
        y="31"
        width="10"
        height="4.5"
        rx="1.6"
        transform="rotate(-18 34 33)"
        fill="#fde68a"
      />
      <path
        d="M11 41c5-1.4 21-1.4 26 0"
        stroke="#fff"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeOpacity=".45"
      />
    </svg>
  );
}
